'use client'
import { useMemo, useState, useCallback, type FC } from 'react'
import { useParams } from 'next/navigation'
import styled from 'styled-components'
import { motion, AnimatePresence } from 'framer-motion'
import { hex2Rgba, mq, vw } from '@/styles'
import { useTranslator, useMount } from '@/hooks'
import { groupedByCategory } from '@/utils'
import type { TypeCMSExample, TypeCategoryOrdered } from '@/schema'
import { Figure } from './Figure'
import { Link } from './Link'

const itemsPerPage = 24

const getSubjects = (data, onClickSubjectType, genderClicked, lang) => {
  if(Array.isArray(data)) {
    return data.filter(el => el.value !== 'other').map((item, idx) => {
      return (
        <li key={`${idx}-${item.value}`} >
          <Link className={item.value === genderClicked ? 'active' : ''} href={`/${lang}/gallery/${item.value}`} onClick={() => { onClickSubjectType(item.value)}}>
            {item.name}
          </Link>
        </li>
      )
    })
  } else return []
}

const getExamplesCategories = (data, onClick, categoryClicked, textAllTranslated) => data.map((item, idx) => {
  const { category, emoji } = item

  return (<li key={`${idx}`}>
    <button type='button' onClick={() => {onClick(idx) }} className={ `${(data[categoryClicked]?.category === category) ? 'active': ''}`}>
      {emoji && <span>{emoji}</span> }{category.code === 'all' ? textAllTranslated : category}
    </button>
  </li>)
})

const getExample = (data) => {
  return data.map((element, idx) => {
    const {image} = element
    return(
      <li key={idx}>
        <Figure media={image} />
      </li>
    )
  })
}

const getGenderExamples = (data, parentClicked, itemAmountDisplayed) => {
  return data.map((item, idx) => {
    const Examples = getExample(item.examples.slice(0, itemAmountDisplayed))
    return (
      <LiStyled key={idx} className={parentClicked === idx ? 'show' : ''} variants={variantsLi} initial='initial' animate={parentClicked === idx ? 'animate' : 'exit'} exit='exit'>
        <ul>
          {Examples}
        </ul>
      </LiStyled>
    )
  })
}

const getChildren = ( data, parentClicked, subjectType, itemAmountDisplayed ) => {
  const GenderExamples = getGenderExamples(data, parentClicked, itemAmountDisplayed)
  return (
    <UlGenderStyled key={subjectType + parentClicked} className={'show'} data-gender={subjectType} variants={variants} initial='hide' animate={'show'}>
      {GenderExamples}
    </UlGenderStyled>
  )
}

export const SectionGallery: FC<{ data: TypeCMSExample[], filterSubjectType?: string }> = ({ data, filterSubjectType }) => {
  const isMount = useMount()
  const { t } = useTranslator()
  const { lang } = useParams()
  const [subjectType, setSubjectTypeClicked] = useState(filterSubjectType ? filterSubjectType : 'all')
  const [categoryClicked, setCategoryClicked] = useState(0)
  const [itemAmountDisplayed, setItemAmountDisplayed] = useState(itemsPerPage) // Number of visible items

  const loadMore = () => {
    setItemAmountDisplayed(prev => prev + itemsPerPage)
  }

  const onClickSubjectType = useCallback(async (value) => {
    setSubjectTypeClicked(value)
    setCategoryClicked(0)
    setItemAmountDisplayed(itemsPerPage)
  }, [])

  const onClick = useCallback(async (position) => {
    setCategoryClicked(position)
    setItemAmountDisplayed(itemsPerPage)
  }, [])


  const SubjectTypes = useMemo(() => getSubjects(t('modal.new_project.steps.two.radio'), onClickSubjectType, subjectType, lang), [t, onClickSubjectType, subjectType, lang])
  const dataBySubjectType = useMemo(() => (
    {
      man: data.filter((el) => el.subjectType.value.toLowerCase() === 'man'),
      woman: data.filter((el) => el.subjectType.value.toLowerCase() === 'woman'),
      girl: data.filter((el) => el.subjectType.value.toLowerCase() === 'girl'),
      boy: data.filter((el) => el.subjectType.value.toLowerCase() === 'boy'),
      cat: data.filter((el) => el.subjectType.value.toLowerCase() === 'cat'),
      dog: data.filter((el) => el.subjectType.value.toLowerCase() === 'dog'),
      all: data
    }
  ),[data])


  const orderedByCategories = useMemo(() =>  Object.values(groupedByCategory(Array.isArray(dataBySubjectType[subjectType]) ? dataBySubjectType[subjectType] : [], true)) as unknown as TypeCategoryOrdered, [dataBySubjectType, subjectType])
  const ExamplesCategories = useMemo(() => getExamplesCategories(orderedByCategories, onClick, categoryClicked, t('commons.gallery.all')), [orderedByCategories, onClick, categoryClicked, t])
  const SugestionsImages = useMemo(() => getChildren(orderedByCategories, categoryClicked, subjectType, itemAmountDisplayed), [orderedByCategories, categoryClicked, subjectType, itemAmountDisplayed])

  return (
    <Section>
      <ul>{SubjectTypes}</ul>
      {subjectType !== 'cat' && subjectType !== 'dog' && <ul>{ExamplesCategories}</ul>}
      {isMount && <AnimatePresence>
        {SugestionsImages}
      </AnimatePresence>}
      {orderedByCategories[categoryClicked]?.examples?.length  > itemAmountDisplayed && (
        <button className='load' onClick={loadMore}>{t('commons.actions.load_more')}</button>
      )}
    </Section>
  )
}

const Section = styled.section`
  align-items: center;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  max-width: 100%;
  min-height: 700px;
  padding: ${vw(100, 'mobile')} 0 ;
  position: relative;
  z-index: 2;

  ${mq.greaterThan('nexus7')} {
    padding: ${vw(100, 'tablet')} 0;
  }

  ${mq.greaterThan('tablet')} {
    align-items: unset;
    padding: 130px 0 150px;
  }

  ${mq.greaterThan('desktop')} {
    max-width: 1440px;
  }

  > ul:not([data-gender]) {
    align-items: center;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: ${vw(10, 'mobile')};
    justify-content: center;
    margin: 0 ${vw(15, 'mobile')} ${vw(10, 'mobile')} ;

    ${mq.greaterThan('nexus7')} {
      gap: ${vw(10, 'tablet')};
      margin: 0 ${vw(15, 'tablet')} ${vw(10, 'tablet')} ;
    }

    ${mq.greaterThan('tablet')} {
      gap: ${vw(10, 'desktop')};
      justify-content: flex-start;
      margin: 0 ${vw(25, 'desktop')} ${vw(10, 'desktop')} ;
    }

    ${mq.greaterThan('tablet')} {
      gap: 10px;
      margin: 0 25px 10px;
    }
  }

  figure {
    background-color:${({ theme }) => hex2Rgba(theme.colors.neon_purple, .2)};
  }

  a,
  button {
    background-color: ${({ theme }) => hex2Rgba(theme.colors.white, .1)};
    border-radius: 50px;
    display: inline-block;
    flex-direction: row;
    font-size: 15px;
    font-weight: 400;
    gap: unset;
    height: 35px;
    justify-content: center;
    line-height: 15px;
    opacity: 1;
    padding:10px 15px;
    transition: 225ms all ease;

    &.active {
      background-color: ${({ theme }) => theme.colors.white};
      color: ${({ theme }) => theme.colors.neon_purple};
    }

    &:hover {
      background-color: ${({ theme }) => theme.colors.white};
      color: ${({ theme }) => theme.colors.neon_purple};
      opacity: .9;
    }

    span {
      margin-right: ${vw(5, 'mobile')};

      ${mq.greaterThan('nexus7')} {
        margin-right: ${vw(5, 'tablet')};
      }

      ${mq.greaterThan('tablet')} {
        margin-right: ${vw(5, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
        margin-right: 5px;
      }
    }
  }

  .load {
    margin: 60px auto 0;
    width: 30%;
  }
`

const LiStyled = styled(motion.li)<any>`
  display: none;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${vw(12, 'mobile')};
  margin:${vw(20, 'mobile')} ${vw(25, 'mobile')} 0;

  ${mq.greaterThan('nexus7')} {
    gap: ${vw(25, 'tablet')};
    margin:${vw(20, 'tablet')} ${vw(15, 'tablet')} 0;
  }

  ${mq.greaterThan('tablet')} {
    gap: ${vw(25, 'desktop')};
    margin:${vw(20, 'desktop')} ${vw(25, 'desktop')} 0;
  }

  ${mq.greaterThan('desktop')} {
    gap: 25px;
    margin: 20px 25px 0;
  }

  &.show {
    display: flex;
    justify-content: flex-start;

    ${mq.greaterThan('nexus7')} {
      justify-content: center;
    }

    ${mq.greaterThan('tablet')} {
      justify-content: unset;
    }
  }

  > ul {
    align-items: center;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: ${vw(10, 'mobile')};
    justify-content: center;

    ${mq.greaterThan('nexus7')} {
      gap: ${vw(10, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      gap: ${vw(10, 'desktop')};
      justify-content: flex-start;
    }

    ${mq.greaterThan('tablet')} {
      gap: 10px;
    }

    > li {
      figure {
        background-color:${({ theme }) => hex2Rgba(theme.colors.neon_purple, .2)};
      }

      figure,
      img {
        border-radius: ${vw(10, 'mobile')};
        height: ${vw(175, 'mobile')};
        width: ${vw(175, 'mobile')};

        ${mq.greaterThan('nexus7')} {
          border-radius: ${vw(10, 'tablet')};
          height: ${vw(175, 'tablet')};
          width: ${vw(175, 'tablet')};
        }

        ${mq.greaterThan('tablet')} {
          border-radius: ${vw(10, 'desktop')};
          height: ${vw(328, 'desktop')};
          width: ${vw(328, 'desktop')};
        }

        ${mq.greaterThan('desktop')} {
          border-radius: 10px;
          height: 328px;
          width: 328px;
        }
      }
    }
  }
`

const UlGenderStyled = styled(motion.ul)<any>`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`

const variants = {
  show: {
    opacity: 1,
    transition: {
      ease: 'easeOut',
      duration: 0.3
    }
  },
  hide: {
    opacity: 0,
    transition: {
      ease: 'easeOut',
      duration: 0.3
    }
  }
}

const variantsLi = {
  initial: {
    opacity: 0,
    transition: {
      duration: 0
    }
  },
  animate: {
    opacity: 1,
    transition: {
      duration: .8,
      ease: 'easeOut'
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: .3,
      ease: 'easeOut'
    }
  }
}
